import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { useCreateReachProSupportCase } from 'src/hooks/api/useCreateReachProSupportCase';
import { ContentId } from 'src/utils/constants/contentId';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../Modal';
import * as styles from './CreateReachProSupportCaseModal.css';

export type CreateReachProSupportCaseModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  initialTopic?: string;
  initialEventId?: number;
  initialListingId?: number;
  initialSaleId?: number;
  onCreated?: (workItemId: number) => void;
};

export function CreateReachProSupportCaseModal({
  isOpen,
  onClose,
  initialTopic = '',
  initialEventId,
  initialListingId,
  initialSaleId,
  onCreated,
}: CreateReachProSupportCaseModalProps) {
  const navigate = useNavigate();

  const [topic, setTopic] = useState(initialTopic);
  const [eventId, setEventId] = useState(initialEventId?.toString() ?? '');
  const [listingId, setListingId] = useState(
    initialListingId?.toString() ?? ''
  );
  const [saleId, setSaleId] = useState(initialSaleId?.toString() ?? '');

  // Sync state with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setTopic(initialTopic);
      setEventId(initialEventId?.toString() ?? '');
      setListingId(initialListingId?.toString() ?? '');
      setSaleId(initialSaleId?.toString() ?? '');
    }
  }, [isOpen, initialTopic, initialEventId, initialListingId, initialSaleId]);

  const createSupportCase = useCreateReachProSupportCase();

  const isValid =
    topic.trim() !== '' && eventId.trim() !== '' && !isNaN(parseInt(eventId));

  const handleCreate = async () => {
    if (!isValid) {
      return;
    }

    const supportCase = await createSupportCase.mutateAsync({
      topic: topic.trim(),
      eventId: parseInt(eventId),
      listingId: listingId ? parseInt(listingId) : null,
      saleId: saleId ? parseInt(saleId) : null,
    });

    if (!supportCase) {
      return;
    }

    onClose?.();

    if (onCreated) {
      onCreated(supportCase.workItemId);
    } else {
      navigate(`/reachpro-support/${supportCase.workItemId}`);
    }

    setTopic('');
    setEventId('');
    setListingId('');
    setSaleId('');
  };

  const handleClose = () => {
    setTopic('');
    setEventId('');
    setListingId('');
    setSaleId('');
    onClose?.();
  };

  const issueWithTicketDeliveryPlaceholder = useContent(
    ContentId.IssueWithTicketDelivery
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      unmountOnClose
      centered
      size="sm"
    >
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerText}>
          <Content id={ContentId.CreateSupportConversation} />
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.modalBody}>
          <Stack direction="column" gap="l">
            <div className={styles.fieldLabel}>
              <Content id={ContentId.Subject} />
              <span className={styles.required}>*</span>
            </div>
            <PosTextField
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={issueWithTicketDeliveryPlaceholder}
              maxLength={500}
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.StubHubEventId} />
              <span className={styles.required}>*</span>
            </div>
            <PosTextField
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.ListingId} />
            </div>
            <PosTextField
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.SaleId} />
            </div>
            <PosTextField
              value={saleId}
              onChange={(e) => setSaleId(e.target.value)}
              type="number"
            />
          </Stack>
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton
          variant="regular"
          textContentId={ContentId.Cancel}
          onClick={handleClose}
        />
        <OkButton
          variant="regular"
          textContentId={ContentId.Create}
          onClick={handleCreate}
          disabled={!isValid || createSupportCase.isPending}
        />
      </ModalFooter>
    </Modal>
  );
}
